import React, {useCallback, useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore} from 'react';
import './PhotoEditor.css';
import {Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle} from '@mui/material';
import {ArrowForward, Create, Delete, KeyboardArrowDown, KeyboardArrowUp, Redo, Refresh, Undo} from '@mui/icons-material';
import {
  COLOR_HEX, COLOR_NAME, docToJson, emptyDoc, Mark, MARK_COLORS, MARK_SIZES, MarkColor, MarkSize, MarkupDoc, parseDoc,
  STICKER_KINDS, StickerKind, STICKERS, TextMark,
} from '../../../shared/photo-markup/markup';
import {drawStickerUnit, MarkupRenderer, OUTLINE, OUTLINE_TEXT, PLACEHOLDER, Point, stickerAngle, TEXT_FONT_FAMILY} from '../../../shared/photo-markup/renderer';
import {canTurn, colorOf, ERASER_ASPECT, Handle, HandleKind, PhotoMarkupState} from '../../../shared/photo-markup/state';
import {getImage, PhotoEdit} from '../../../shared/services/image-service';
import {logError} from '../../../shared/services/logger.service';

/**
 * SQ-562: the web photo editor, the same editor as Android's
 * `PhotoEditorSheet.kt` (SQ-286): full screen, the photo as large as it can
 * be, and every mark (freehand, text, symbols) a separate object until Done.
 * It opens a photo from its ORIGINAL plus its saved marks (GET images/{id}),
 * the same document the phone writes, so marks from either app can still be
 * moved, changed, deleted or reset away on the other.
 *
 * Pointer input: a mouse, a pen or one finger draws, erases or moves; two
 * fingers, or the scroll wheel / trackpad pinch, zoom and pan.
 */

export const EDITOR_BG = '#111111';
const PANEL_BG = '#FFFFFF';
export const HEADER_BG = '#2A2A2A';
const ERASER_PINK = '#F4A7B9';
const INK = '#222222';
const SUBTLE = '#6B6B6B';
const GREEN = '#4CAF50';
const GREEN_DEEP = '#2E7D32'; // SQ-538: the iOS app's button green
const TOOL_BG = '#F2F4F2';

/** The eraser's half-width on screen: zoom in to erase finer. */
const ERASER_HALF: Record<MarkSize, number> = {s: 8, m: 16, l: 28};

/** Clear space between a fingertip and the near edge of the eraser's working end. */
const ERASER_GAP = 44;

/** Finger to the middle of the eraser's working end (straight up the screen). */
const eraserLead = (size: MarkSize) => ERASER_GAP + ERASER_HALF[size] * ERASER_ASPECT;

const TOUCH_PX = 22;
const HANDLE_TOUCH_PX = 30;
const STALK_PX = 36;
const HANDLE_RADIUS = 14;
const CHROME_PX = 2;

export interface PhotoEditorProps {
  imageId: number;
  /** The photo as shown now (the edited copy, if any). */
  imageUrl: string;
  readonly: boolean;
  isSaving: boolean;
  onClose: () => void;
  onSave: (id: number, edit: PhotoEdit) => void;
}

/** What one pointer is doing in the current gesture. */
type OneFinger =
  | {kind: 'stroke'}
  | {kind: 'erase'; lead: Point}
  | {kind: 'moveMark'; id: string}
  | {kind: 'dragHandle'; handle: HandleKind}
  | {kind: 'panOrTap'};

interface Gesture {
  pointerId: number;
  finger: OneFinger;
  down: Point;
  last: Point;
  moved: boolean;
  multi: boolean;
  slop: number;
}

/** Loads a photo for drawing on a canvas (CORS-clean, past any stale cached copy). */
// The photo is fetched with CORS and past the HTTP cache, then drawn from a
// local object URL. An <img crossOrigin> load could be answered from a copy a
// plain <img> cached without CORS headers (stackoverflow.com/questions/55158189),
// which used to be dodged with a cache-busting query; a photo link is signed
// (SQ-735) and an added parameter voids it, so the cache is skipped instead.
const loadImage = async (url: string): Promise<HTMLImageElement> => {
  const response = await fetch(url, {mode: 'cors', cache: 'no-store'});
  if (!response.ok) throw new Error(`Photo answered ${response.status}`);
  const objectUrl = URL.createObjectURL(await response.blob());
  try {
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = e => reject(e);
      img.src = objectUrl;
    });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};

/** The base photo turned by `rotation` degrees, as a canvas to draw from. */
const rotated = (img: HTMLImageElement, rotation: number): HTMLCanvasElement => {
  const turn = ((rotation % 360) + 360) % 360;
  const swap = turn === 90 || turn === 270;
  const c = document.createElement('canvas');
  c.width = swap ? img.naturalHeight : img.naturalWidth;
  c.height = swap ? img.naturalWidth : img.naturalHeight;
  const ctx = c.getContext('2d')!;
  ctx.translate(c.width / 2, c.height / 2);
  ctx.rotate(turn * Math.PI / 180);
  ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
  return c;
};

/**
 * Decides what the editor opens: the server's original + marks; offline or
 * on a photo the server cannot find, the photo as shown, edited flat.
 */
const openPhoto = async (imageId: number, imageUrl: string): Promise<PhotoMarkupState> => {
  try {
    const fresh = await getImage(imageId);
    const original = fresh.originalImageUrl || fresh.imageUrl;
    const parsed = parseDoc(fresh.annotations);
    // No marks yet: start from the photo as shown (the original, or an older
    // web editor's flat edit, which Reset can still undo).
    const doc: MarkupDoc = parsed ? {...parsed, base: parsed.base ?? original} : emptyDoc(fresh.imageUrl);
    return new PhotoMarkupState(doc, original);
  } catch (e) {
    logError('Error opening photo marks; editing flat', {imageId}, e as Error);
    return new PhotoMarkupState(emptyDoc(imageUrl), imageUrl);
  }
};

export const PhotoEditor = (props: PhotoEditorProps) => {
  const {imageId, imageUrl, readonly, isSaving, onClose, onSave} = props;
  const [state, setState] = useState<PhotoMarkupState | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [rendering, setRendering] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [menuHidden, setMenuHidden] = useState(false);
  const images = useRef(new Map<string, HTMLImageElement>());
  const [shown, setShown] = useState<{key: string; rotation: number; canvas: HTMLCanvasElement} | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let live = true;
    (readonly ? Promise.resolve(new PhotoMarkupState(emptyDoc(imageUrl), null)) : openPhoto(imageId, imageUrl))
      .then(s => live && setState(s));
    return () => {
      live = false;
    };
  }, [imageId, imageUrl, readonly]);

  useSyncExternalStore(state?.subscribe ?? noopSubscribe, state?.getVersion ?? zero);

  const baseKey = state?.doc.base ?? null;
  const rotation = state?.doc.rotation ?? 0;

  // The base photo (original, or a flat edit), loaded once per base, then
  // turned to the doc's rotation.
  useEffect(() => {
    if (!baseKey || !state) return;
    let live = true;
    const cached = images.current.get(baseKey);
    (cached ? Promise.resolve(cached) : loadImage(baseKey))
      .then(img => {
        if (!live) return;
        images.current.set(baseKey, img);
        const canvas = rotated(img, rotation);
        setShown({key: baseKey, rotation, canvas});
        state.setPhotoSize(canvas.width, canvas.height);
      })
      .catch(error => {
        logError('Error loading image', {imageId, error});
        if (live) setLoadFailed(true);
      });
    return () => {
      live = false;
    };
  }, [baseKey, rotation, state, imageId]);

  const ready = shown && shown.key === baseKey && shown.rotation === rotation ? shown : null;
  const busy = isSaving || rendering;

  useEffect(() => {
    rootRef.current?.focus();
  }, []);

  const requestCancel = useCallback(() => {
    if (state?.isDirty) setConfirmCancel(true);
    else onClose();
  }, [state, onClose]);

  const save = () => {
    const s = state;
    const photo = ready;
    if (!s || !photo) return;
    s.stopEditingText();
    const cleaned = (d: MarkupDoc): MarkupDoc => ({...d, marks: d.marks.filter(m => !(m.type === 'text' && !m.text.trim()))});
    const final = cleaned(s.doc);
    if (docToJson(final) === docToJson(cleaned(s.initialDoc))) {
      onClose();
      return;
    }
    if (!final.marks.length && final.rotation === 0 && final.base && final.base === s.originalUrl) {
      onSave(imageId, {kind: 'reset'});
      return;
    }
    setRendering(true);
    try {
      const out = document.createElement('canvas');
      out.width = photo.canvas.width;
      out.height = photo.canvas.height;
      const ctx = out.getContext('2d')!;
      ctx.drawImage(photo.canvas, 0, 0);
      new MarkupRenderer(out.width, out.height).draw(ctx, final.marks);
      out.toBlob(blob => {
        setRendering(false);
        if (!blob) {
          logError('No blob rendering the edited photo', {imageId});
          return;
        }
        const file = new File([blob], 'edited-photo.jpg', {type: 'image/jpeg'});
        // A doc without a base has no original to reopen from: send it flat.
        onSave(imageId, {kind: 'render', file, annotations: final.base ? docToJson(final) : undefined});
      }, 'image/jpeg', 0.92);
    } catch (e) {
      setRendering(false);
      logError('Error rendering the edited photo', {imageId}, e as Error);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!state || readonly) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
      return;
    }
    const typing = state.editingText !== null;
    const mod = e.ctrlKey || e.metaKey;
    if (e.key === 'Escape') {
      e.stopPropagation();
      if (typing) state.stopEditingText();
      else if (state.showStickers) state.toggleStickers();
      else if (state.selected) state.select(null);
      else requestCancel();
      return;
    }
    if (typing) return;
    if (mod && e.key.toLowerCase() === 'z') {
      e.preventDefault();
      if (e.shiftKey) state.redo(); else state.undo();
    } else if (mod && e.key.toLowerCase() === 'y') {
      e.preventDefault();
      state.redo();
    } else if ((e.key === 'Delete' || e.key === 'Backspace') && state.selected) {
      e.preventDefault();
      state.deleteSelected();
    }
  };

  const showPanel = state && !readonly && (!menuHidden || state.editingText !== null);
  const card = useCardSize(state && state.photoW > 0 ? state.photoW / state.photoH : null, readonly);

  return (
    <div
      ref={rootRef}
      tabIndex={-1}
      onKeyDown={onKeyDown}
      // A click on the dimmed page around the window is Cancel: it asks
      // before discarding marks, so a stray click cannot lose them.
      onPointerDown={e => {
        if (e.target === e.currentTarget) {
          if (readonly) onClose(); else requestCancel();
        }
      }}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%',
        outline: 'none', userSelect: 'none',
      }}
    >
     <div
      data-testid="photo-editor"
      style={{
        display: 'flex', flexDirection: 'column', width: card.width, height: card.height, background: EDITOR_BG,
        borderRadius: card.full ? 0 : 12, overflow: 'hidden', boxShadow: card.full ? 'none' : '0 12px 48px rgba(0,0,0,0.5)',
      }}
    >
      <TopBar
        readonly={readonly}
        canUndo={!!state?.canUndo && !busy}
        canRedo={!!state?.canRedo && !busy}
        canReset={!!state?.canReset && !busy}
        canSave={!!ready && !busy}
        saving={busy}
        onCancel={readonly ? onClose : requestCancel}
        onUndo={() => state?.undo()}
        onRedo={() => state?.redo()}
        onReset={() => setConfirmReset(true)}
        onDone={save}
      />
      {/* Clipped: a zoomed photo (and its marks) stays in this area, under
          the top bar and the menu, never drawn over them. */}
      <div style={{flex: 1, position: 'relative', overflow: 'hidden', minHeight: 0}}>
        {loadFailed ? (
          <Centered><span style={{color: 'white'}}>Couldn't load this photo.</span></Centered>
        ) : !state || !ready ? (
          <Centered><CircularProgress sx={{color: 'white'}}/></Centered>
        ) : (
          <>
            <MarkupCanvas state={state} photo={ready.canvas} enabled={!busy} readonly={readonly}/>
            {state.editingText && <TextEditOverlay state={state} mark={state.editingText}/>}
          </>
        )}
        {state?.isZoomed && <CanvasChip label="Fit photo" style={{top: 0, right: 0}} onClick={() => state.fitView()}/>}
        {state && ready && !readonly && !state.editingText && (menuHidden
          ? <CanvasChip label="Show menu" icon={<KeyboardArrowUp sx={{fontSize: 18}}/>} style={{bottom: 0, left: '50%', transform: 'translateX(-50%)'}} onClick={() => setMenuHidden(false)}/>
          : <CanvasChip label="Hide menu" icon={<KeyboardArrowDown sx={{fontSize: 18}}/>} style={{bottom: 0, right: 0}} onClick={() => setMenuHidden(true)}/>)}
        {busy && <Centered><CircularProgress sx={{color: 'white'}}/></Centered>}
      </div>
      {showPanel && <BottomPanel state={state!} enabled={!!ready && !busy}/>}
     </div>

      <Dialog open={confirmCancel} onClose={() => setConfirmCancel(false)}>
        <DialogTitle>Discard your changes?</DialogTitle>
        <DialogContent><DialogContentText>Your marks on this photo won't be saved.</DialogContentText></DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmCancel(false)}>Keep editing</Button>
          <Button onClick={() => {
            setConfirmCancel(false);
            onClose();
          }}>Discard</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={confirmReset} onClose={() => setConfirmReset(false)}>
        <DialogTitle>Start over?</DialogTitle>
        <DialogContent>
          <DialogContentText>This takes every mark off and puts the original photo back. You can still Undo, or Cancel without saving.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmReset(false)}>Cancel</Button>
          <Button onClick={() => {
            setConfirmReset(false);
            state?.reset();
          }}>Reset</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

/** Below this screen width (a phone) the window fills the screen. */
export const PHONE_WIDTH = 600;
/** The window's least width on a computer: the tool panel's six tools and its colors-and-sizes row. */
const MIN_CARD_WIDTH = 600;
/** Room the top bar and the tool panel take inside the window, for sizing it to the photo. */
const BARS_HEIGHT = 52 + 140;

export const useViewport = () => {
  const read = () => ({w: window.innerWidth, h: window.innerHeight});
  const [size, setSize] = useState(read);
  useEffect(() => {
    const onResize = () => setSize(read());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return size;
};

/**
 * The editor's window: on a phone the whole screen; on a tablet or computer
 * a card about 90% of the screen's height and as wide as the photo needs at
 * that height (no blank sides), never narrower than the tool panel nor wider
 * than 95% of the screen. Reshapes when the photo is turned; zoom happens
 * inside it.
 */
const useCardSize = (aspect: number | null, readonly: boolean): {width: number | string; height: number | string; full: boolean} => {
  const {w, h} = useViewport();
  if (w < PHONE_WIDTH) return {width: '100%', height: '100%', full: true};
  const maxW = w * 0.95;
  let height = h * 0.9;
  const bars = readonly ? 52 : BARS_HEIGHT;
  let width = aspect ? aspect * (height - bars) : MIN_CARD_WIDTH;
  if (width > maxW) {
    // A wide photo: as wide as allowed, and only as tall as it needs.
    width = maxW;
    height = Math.min(height, width / aspect! + bars);
  }
  return {width: Math.max(Math.min(MIN_CARD_WIDTH, maxW), width), height, full: false};
};

const noopSubscribe = () => () => {};
const zero = () => 0;

const Centered = ({children}: {children: React.ReactNode}) => (
  <div style={{position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none'}}>{children}</div>
);

/** A small dark button floating over the photo. */
const CanvasChip = ({label, icon, style, onClick}: {label: string; icon?: React.ReactNode; style: React.CSSProperties; onClick: () => void}) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      position: 'absolute', margin: 8, display: 'flex', alignItems: 'center', gap: 4, border: 'none', cursor: 'pointer',
      borderRadius: 999, background: 'rgba(0,0,0,0.8)', color: 'white', padding: '6px 12px', fontSize: 13, fontWeight: 600, ...style,
    }}
  >
    {icon}{label}
  </button>
);

const TopBar = (p: {
  readonly: boolean; canUndo: boolean; canRedo: boolean; canReset: boolean; canSave: boolean; saving: boolean;
  onCancel: () => void; onUndo: () => void; onRedo: () => void; onReset: () => void; onDone: () => void;
}) => {
  const barButton = (enabled: boolean): React.CSSProperties => ({
    display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', padding: '6px', fontSize: 15,
    color: enabled ? 'white' : 'rgba(255,255,255,0.4)', cursor: enabled ? 'pointer' : 'default', fontFamily: 'inherit',
  });
  return (
    <div style={{display: 'flex', alignItems: 'center', height: 52, minHeight: 52, background: HEADER_BG, padding: '0 4px'}}>
      <button type="button" style={barButton(true)} onClick={p.onCancel}>{p.readonly ? 'Close' : 'Cancel'}</button>
      <div style={{flex: 1}}/>
      {!p.readonly && (
        <>
          <button type="button" style={barButton(p.canUndo)} disabled={!p.canUndo} onClick={p.onUndo}><Undo sx={{fontSize: 20}}/>Undo</button>
          <button type="button" style={barButton(p.canRedo)} disabled={!p.canRedo} onClick={p.onRedo}><Redo sx={{fontSize: 20}}/>Redo</button>
          <button type="button" style={barButton(p.canReset)} disabled={!p.canReset} onClick={p.onReset}>Reset</button>
          <button
            type="button"
            disabled={!p.canSave}
            onClick={p.onDone}
            style={{
              marginLeft: 4, border: 'none', borderRadius: 999, padding: '7px 18px', fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
              background: p.canSave ? GREEN_DEEP : 'rgba(46,125,50,0.4)', color: 'white', cursor: p.canSave ? 'pointer' : 'default',
            }}
          >
            {p.saving ? 'Saving…' : 'Done'}
          </button>
        </>
      )}
    </div>
  );
};

/** The photo and its marks: a canvas sized to its box at the screen's pixel density. */
const MarkupCanvas = ({state, photo, enabled, readonly}: {state: PhotoMarkupState; photo: HTMLCanvasElement; enabled: boolean; readonly: boolean}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const gesture = useRef<Gesture | null>(null);
  const pointers = useRef(new Map<number, Point>());
  const pinch = useRef<{centroid: Point; dist: number} | null>(null);
  const [mouseInEraser, setMouseInEraser] = useState(false);

  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!box) return;
    const fit = () => state.setCanvasSize(box.clientWidth, box.clientHeight);
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(box);
    return () => ro.disconnect();
  }, [state]);

  // Paint on every change, at most once a frame.
  const version = state.version;
  useEffect(() => {
    const id = requestAnimationFrame(() => paint(canvasRef.current, state, photo));
    return () => cancelAnimationFrame(id);
  }, [state, photo, version]);

  const local = (e: {clientX: number; clientY: number}): Point => {
    const r = canvasRef.current!.getBoundingClientRect();
    return {x: e.clientX - r.left, y: e.clientY - r.top};
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (!enabled) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    canvasRef.current?.setPointerCapture(e.pointerId);
    const p = local(e);
    pointers.current.set(e.pointerId, p);
    const g = gesture.current;
    if (g) {
      // A second finger: this is a zoom, not a mark.
      if (!g.multi) {
        g.multi = true;
        switch (g.finger.kind) {
          case 'stroke': state.strokeCancel(); break;
          case 'erase': state.eraseCancel(); break;
          case 'moveMark':
          case 'dragHandle': state.cancelChange(); break;
        }
      }
      pinch.current = pinchOf(pointers.current);
      return;
    }
    const touch = e.pointerType === 'touch';
    let finger: OneFinger;
    if (readonly) {
      finger = {kind: 'panOrTap'};
    } else if (state.tool === 'draw') {
      state.strokeStart(p);
      finger = {kind: 'stroke'};
    } else if (state.tool === 'erase') {
      // Held like a tool: under a finger, its working end rides ahead of the
      // fingertip; a mouse pointer covers nothing, so it erases right there.
      const lead: Point = touch ? {x: 0, y: -eraserLead(state.eraserSize)} : {x: 0, y: 0};
      state.eraserReachPx = Math.hypot(lead.x, lead.y);
      state.eraseStart(add(p, lead));
      finger = {kind: 'erase', lead};
    } else {
      const handle = state.handleAt(p, STALK_PX, HANDLE_TOUCH_PX);
      const mark = handle ? null : state.markAt(p, TOUCH_PX);
      finger = handle ? {kind: 'dragHandle', handle} : mark ? {kind: 'moveMark', id: mark.id} : {kind: 'panOrTap'};
    }
    if (finger.kind === 'dragHandle') state.beginChange();
    gesture.current = {
      pointerId: e.pointerId, finger, down: p, last: p, moved: false, multi: false,
      slop: touch ? 8 : 3,
    };
    // A mouse pointer covers nothing, so it erases at once; a finger waits
    // until it moves (putting it down shows where the eraser is).
    if (finger.kind === 'erase' && !touch) state.eraseTo(p, ERASER_HALF[state.eraserSize]);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const p = local(e);
    const g = gesture.current;
    if (!g) {
      // A mouse over the photo with the eraser picked: the eraser is the pointer.
      if (e.pointerType === 'mouse' && state.tool === 'erase' && !readonly) {
        state.eraseHover(p);
        if (!mouseInEraser) setMouseInEraser(true);
      }
      return;
    }
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, p);
    if (g.multi) {
      const now = pinchOf(pointers.current);
      const before = pinch.current;
      if (now && before) {
        state.zoomPan(before.dist > 0 ? now.dist / before.dist : 1, sub(now.centroid, before.centroid), now.centroid);
      }
      pinch.current = now;
      return;
    }
    if (e.pointerId !== g.pointerId) return;
    const delta = sub(p, g.last);
    if (!g.moved && (g.finger.kind === 'stroke' || Math.hypot(p.x - g.down.x, p.y - g.down.y) > g.slop)) {
      g.moved = true;
      if (g.finger.kind === 'moveMark') {
        const id = g.finger.id;
        const m = state.doc.marks.find(x => x.id === id);
        if (m) state.select(m);
        state.beginChange();
      }
    }
    if (g.moved) {
      switch (g.finger.kind) {
        case 'stroke': state.strokeTo(p); break;
        case 'erase': state.eraseTo(add(p, g.finger.lead), ERASER_HALF[state.eraserSize]); break;
        case 'moveMark': state.dragMark(g.finger.id, delta); break;
        case 'dragHandle': state.dragHandle(g.finger.handle, p); break;
        case 'panOrTap': state.zoomPan(1, delta, p); break;
      }
    } else if (g.finger.kind === 'erase') {
      // The finger is still settling: the eraser follows, erasing nothing.
      state.eraseHover(add(p, g.finger.lead));
    }
    g.last = p;
  };

  const onPointerEnd = (e: React.PointerEvent, cancelled: boolean) => {
    pointers.current.delete(e.pointerId);
    const g = gesture.current;
    if (!g) return;
    if (g.multi) {
      pinch.current = pinchOf(pointers.current);
      if (pointers.current.size === 0) gesture.current = null;
      return;
    }
    if (e.pointerId !== g.pointerId) return;
    gesture.current = null;
    const mouse = e.pointerType === 'mouse';
    if (cancelled) {
      switch (g.finger.kind) {
        case 'stroke': state.strokeCancel(); break;
        case 'erase': state.eraseCancel(); break;
        case 'moveMark':
        case 'dragHandle': state.cancelChange(); break;
      }
      return;
    }
    switch (g.finger.kind) {
      case 'stroke': state.strokeEnd(); break;
      case 'erase': state.eraseEnd(mouse); break;
      case 'dragHandle': state.endChange(); break;
      case 'moveMark':
        if (g.moved) {
          state.endChange();
        } else {
          // A click on text types into it; on anything else, picks it.
          const id = g.finger.id;
          const m = state.doc.marks.find(x => x.id === id) ?? null;
          if (m?.type === 'text') state.editText(m); else state.select(m);
        }
        break;
      case 'panOrTap':
        if (!g.moved && !readonly) {
          state.select(null);
          state.showStickers = false;
          state.touch();
        }
        break;
    }
  };

  // Scroll wheel and trackpad pinch zoom about the pointer (a listener of our
  // own: React's wheel handler is passive and cannot stop the page scrolling).
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const r = canvas.getBoundingClientRect();
      const at = {x: e.clientX - r.left, y: e.clientY - r.top};
      const speed = e.ctrlKey ? 0.01 : 0.0015;
      const lines = e.deltaMode === 1 ? 16 : 1;
      state.zoomPan(Math.exp(-e.deltaY * lines * speed), {x: e.ctrlKey ? 0 : -e.deltaX * lines, y: 0}, at);
    };
    canvas.addEventListener('wheel', onWheel, {passive: false});
    return () => canvas.removeEventListener('wheel', onWheel);
  }, [state]);

  const cursor = readonly ? 'grab'
    : state.tool === 'draw' ? 'crosshair'
      : state.tool === 'erase' && mouseInEraser ? 'none'
        : 'default';

  return (
    <div ref={boxRef} style={{position: 'absolute', inset: 0}}>
      <canvas
        id="drawable-image-canvas"
        ref={canvasRef}
        style={{width: '100%', height: '100%', display: 'block', touchAction: 'none', cursor}}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={e => onPointerEnd(e, false)}
        onPointerCancel={e => onPointerEnd(e, true)}
        onPointerLeave={e => {
          if (e.pointerType === 'mouse' && !gesture.current && state.eraserAt) {
            state.eraseHover(null);
            setMouseInEraser(false);
          }
        }}
      />
    </div>
  );
};

const add = (a: Point, b: Point): Point => ({x: a.x + b.x, y: a.y + b.y});
const sub = (a: Point, b: Point): Point => ({x: a.x - b.x, y: a.y - b.y});

const pinchOf = (pts: Map<number, Point>): {centroid: Point; dist: number} | null => {
  const list = Array.from(pts.values()).slice(0, 2);
  if (list.length < 2) return list.length === 1 ? {centroid: list[0], dist: 0} : null;
  return {
    centroid: {x: (list[0].x + list[1].x) / 2, y: (list[0].y + list[1].y) / 2},
    dist: Math.hypot(list[0].x - list[1].x, list[0].y - list[1].y),
  };
};

/** Draws the whole view: photo and marks in photo space, then the chrome on screen. */
const paint = (canvas: HTMLCanvasElement | null, state: PhotoMarkupState, photo: HTMLCanvasElement) => {
  if (!canvas) return;
  const dpr = window.devicePixelRatio || 1;
  const w = Math.max(1, Math.round(state.canvasW * dpr));
  const h = Math.max(1, Math.round(state.canvasH * dpr));
  if (canvas.width !== w || canvas.height !== h) {
    canvas.width = w;
    canvas.height = h;
  }
  const ctx = canvas.getContext('2d');
  const r = state.renderer;
  if (!ctx || !r) return;
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, w, h);
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  const origin = state.viewOrigin();
  const sc = state.scale;
  ctx.save();
  ctx.translate(origin.x, origin.y);
  ctx.scale(sc, sc);
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(photo, 0, 0);
  // The box being typed into is drawn by its text field instead.
  r.draw(ctx, state.doc.marks, true, state.editingTextId);
  if (state.liveStroke.length) {
    r.drawMark(ctx, {type: 'stroke', id: 'live', color: state.color, size: state.size, points: state.liveStroke, zoom: state.zoom});
  }
  const sel = state.selected;
  if (sel) drawSelectionFrame(ctx, r, sel, sc);
  ctx.restore();
  state.handles(STALK_PX).forEach(hd => drawHandle(ctx, hd));
  if (state.eraserAt) {
    const touchLead = state.eraserReachPx;
    drawHeldEraser(ctx, state.eraserAt, ERASER_HALF[state.eraserSize], touchLead);
  }
};

/** A dashed frame around the selection, drawn in photo space. */
const drawSelectionFrame = (ctx: CanvasRenderingContext2D, r: MarkupRenderer, mark: Mark, scale: number) => {
  const w = CHROME_PX / scale;
  const frame = (x0: number, y0: number, x1: number, y1: number) => {
    ctx.setLineDash([]);
    ctx.lineWidth = w * 2.2;
    ctx.strokeStyle = 'rgba(0,0,0,0.53)';
    ctx.strokeRect(x0, y0, x1 - x0, y1 - y0);
    ctx.setLineDash([w * 4, w * 3]);
    ctx.lineWidth = w;
    ctx.strokeStyle = '#FFFFFF';
    ctx.strokeRect(x0, y0, x1 - x0, y1 - y0);
    ctx.setLineDash([]);
  };
  if (mark.type === 'text' || mark.type === 'sticker') {
    const box = r.localBox(mark);
    if (!box) return;
    const angle = mark.type === 'text' ? mark.angle : stickerAngle(mark);
    ctx.save();
    ctx.translate(r.px(mark.x), r.py(mark.y));
    ctx.rotate(angle * Math.PI / 180);
    frame(box.left, box.top, box.right, box.bottom);
    if (canTurn(mark)) {
      const top = box.top - STALK_PX / scale;
      ctx.lineWidth = w * 2.2;
      ctx.strokeStyle = 'rgba(0,0,0,0.53)';
      ctx.beginPath();
      ctx.moveTo(0, box.top);
      ctx.lineTo(0, top);
      ctx.stroke();
      ctx.setLineDash([w * 4, w * 3]);
      ctx.lineWidth = w;
      ctx.strokeStyle = '#FFFFFF';
      ctx.beginPath();
      ctx.moveTo(0, box.top);
      ctx.lineTo(0, top);
      ctx.stroke();
      ctx.setLineDash([]);
    }
    ctx.restore();
  } else if (mark.type === 'stroke') {
    // Tight around the line, with a hair of air so the dashes don't sit on it.
    const b = r.strokeBounds(mark);
    frame(b.left - w * 3, b.top - w * 3, b.right + w * 3, b.bottom + w * 3);
  }
  // A line mark's two end handles show its selection.
};

/**
 * A grab handle: a white disc whose arrows say what dragging it does — a
 * turning arrow to spin, a two-way arrow to stretch, four-way arrows to move
 * a line's end.
 */
const drawHandle = (ctx: CanvasRenderingContext2D, h: Handle) => {
  const {x, y} = h.screen;
  ctx.fillStyle = 'rgba(0,0,0,0.4)';
  ctx.beginPath();
  ctx.arc(x, y, HANDLE_RADIUS + CHROME_PX, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#FFFFFF';
  ctx.beginPath();
  ctx.arc(x, y, HANDLE_RADIUS, 0, Math.PI * 2);
  ctx.fill();
  const a = h.angle * Math.PI / 180;
  if (h.kind === 'rotate') drawTurnArrow(ctx, h.screen, HANDLE_RADIUS * 0.5);
  else if (h.kind === 'width') drawTwoWayArrow(ctx, h.screen, {x: Math.cos(a), y: Math.sin(a)}, HANDLE_RADIUS * 0.62);
  else {
    drawTwoWayArrow(ctx, h.screen, {x: 1, y: 0}, HANDLE_RADIUS * 0.6);
    drawTwoWayArrow(ctx, h.screen, {x: 0, y: 1}, HANDLE_RADIUS * 0.6);
  }
};

const inkLine = (ctx: CanvasRenderingContext2D, a: Point, b: Point) => {
  ctx.strokeStyle = INK;
  ctx.lineWidth = CHROME_PX;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(a.x, a.y);
  ctx.lineTo(b.x, b.y);
  ctx.stroke();
};

const drawTwoWayArrow = (ctx: CanvasRenderingContext2D, c: Point, u: Point, half: number) => {
  const head = half * 0.5;
  const n = {x: -u.y, y: u.x};
  inkLine(ctx, {x: c.x - u.x * half, y: c.y - u.y * half}, {x: c.x + u.x * half, y: c.y + u.y * half});
  ([[1, -1], [-1, 1]] as [number, number][]).forEach(([sTip, sBack]) => {
    const tip = {x: c.x + u.x * half * sTip, y: c.y + u.y * half * sTip};
    const back = {x: u.x * sBack, y: u.y * sBack};
    inkLine(ctx, tip, {x: tip.x + back.x * head + n.x * head, y: tip.y + back.y * head + n.y * head});
    inkLine(ctx, tip, {x: tip.x + back.x * head - n.x * head, y: tip.y + back.y * head - n.y * head});
  });
};

/** Three-quarters of a circle with an arrow tip: "spin it". */
const drawTurnArrow = (ctx: CanvasRenderingContext2D, c: Point, rr: number) => {
  const start = 150;
  const sweep = 270;
  ctx.strokeStyle = INK;
  ctx.lineWidth = CHROME_PX;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.arc(c.x, c.y, rr, start * Math.PI / 180, (start + sweep) * Math.PI / 180);
  ctx.stroke();
  const t = (start + sweep) * Math.PI / 180;
  const radial = {x: Math.cos(t), y: Math.sin(t)};
  const tangent = {x: -radial.y, y: radial.x};
  const end = {x: c.x + radial.x * rr, y: c.y + radial.y * rr};
  const head = rr * 0.8;
  ctx.fillStyle = INK;
  ctx.beginPath();
  ctx.moveTo(end.x + tangent.x * head, end.y + tangent.y * head);
  ctx.lineTo(end.x + radial.x * head * 0.75, end.y + radial.y * head * 0.75);
  ctx.lineTo(end.x - radial.x * head * 0.75, end.y - radial.y * head * 0.75);
  ctx.closePath();
  ctx.fill();
};

const roundRectPath = (ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) => {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
};

/**
 * The eraser as it is held: a slim see-through handle running down from the
 * pink working end at `tip`, which is what erases. Under a finger the
 * handle reaches the fingertip (`lead` below the tip); under a mouse the
 * tip is the pointer and the handle is drawn at the same length for the
 * same look.
 */
const drawHeldEraser = (ctx: CanvasRenderingContext2D, tip: Point, halfW: number, lead: number) => {
  const line = CHROME_PX;
  const endW = halfW * 2;
  const endH = halfW * 2 * ERASER_ASPECT;
  const handleLen = lead > 0 ? lead : ERASER_GAP + endH / 2;
  const fingertip = {x: tip.x, y: tip.y + handleLen};
  const handleW = Math.min(line * 7, Math.max(line * 4, halfW * 0.7));
  const handleTop = tip.y + endH / 2;
  const handleH = Math.max(0, fingertip.y - handleTop);
  roundRectPath(ctx, tip.x - handleW / 2, handleTop, handleW, handleH, handleW / 2);
  ctx.fillStyle = 'rgba(255,255,255,0.28)';
  ctx.fill();
  ctx.strokeStyle = 'rgba(0,0,0,0.4)';
  ctx.lineWidth = line;
  ctx.stroke();
  // A metal band where the rubber meets the handle, as on a pencil.
  ctx.fillStyle = 'rgba(176,183,189,0.8)';
  ctx.fillRect(tip.x - handleW * 0.75, handleTop - line * 0.5, handleW * 1.5, line * 3);
  if (lead > 0) {
    // Where the finger holds it.
    ctx.strokeStyle = 'rgba(0,0,0,0.4)';
    ctx.lineWidth = line * 1.5;
    ctx.beginPath();
    ctx.arc(fingertip.x, fingertip.y, line * 6, 0, Math.PI * 2);
    ctx.stroke();
  }
  // The working end: see-through pink, so what is under it shows.
  const corner = endH * 0.28;
  roundRectPath(ctx, tip.x - endW / 2, tip.y - endH / 2, endW, endH, corner);
  ctx.fillStyle = 'rgba(244,167,185,0.35)';
  ctx.fill();
  ctx.strokeStyle = 'rgba(0,0,0,0.53)';
  ctx.lineWidth = line * 2.2;
  ctx.stroke();
  ctx.strokeStyle = '#FFFFFF';
  ctx.lineWidth = line;
  ctx.stroke();
};

/**
 * The text box being typed into, as a real text field sitting exactly on the
 * photo where the box is (same size, color, turn and outline the saved photo
 * gets), so the words appear where they will stay.
 */
const TextEditOverlay = ({state, mark}: {state: PhotoMarkupState; mark: TextMark}) => {
  const ref = useRef<HTMLTextAreaElement>(null);
  const r = state.renderer;
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, [mark.id]);
  // Grow with the words, as the box does.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = '0px';
    el.style.height = `${el.scrollHeight}px`;
  });
  if (!r) return null;
  const sc = state.scale;
  const textPx = r.textSizeOf(mark) * sc;
  const widthPx = Math.max(1, Math.round(r.textWidthOf(mark) * sc));
  const center = state.toScreen(r.px(mark.x), r.py(mark.y));
  const fill = COLOR_HEX[mark.color];
  return (
    <textarea
      ref={ref}
      aria-label="Text on photo"
      value={mark.text}
      placeholder={PLACEHOLDER}
      rows={1}
      autoCapitalize="sentences"
      spellCheck={false}
      onChange={e => state.setText(e.target.value)}
      onKeyDown={e => {
        // Enter is the keyboard's Done, as on the phone; Shift+Enter a new line.
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          state.stopEditingText();
        }
      }}
      className="photo-editor-text"
      style={{
        position: 'absolute',
        left: center.x - widthPx / 2,
        top: center.y,
        width: widthPx,
        transform: `translateY(-50%) rotate(${mark.angle}deg)`,
        transformOrigin: '50% 50%',
        margin: 0,
        padding: 0,
        border: 'none',
        outline: 'none',
        resize: 'none',
        overflow: 'hidden',
        background: 'transparent',
        textAlign: 'center',
        font: `bold ${textPx}px ${TEXT_FONT_FAMILY}`,
        lineHeight: 1.171875,
        color: fill,
        caretColor: fill,
        WebkitTextStroke: `${textPx * OUTLINE_TEXT}px ${OUTLINE}`,
        paintOrder: 'stroke fill',
        // The placeholder in the mark's color, faint, as the phone shows it.
        ['--placeholder-color' as string]: fill,
        userSelect: 'text',
      }}
    />
  );
};

// ---- the bottom panel ---------------------------------------------------------

const Hint = ({children, style}: {children: React.ReactNode; style?: React.CSSProperties}) => (
  <div style={{color: SUBTLE, fontSize: 12, lineHeight: '15px', padding: '2px 0', ...style}}>{children}</div>
);

const DeleteChip = ({onClick}: {onClick: () => void}) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      display: 'flex', alignItems: 'center', gap: 4, borderRadius: 999, border: '1px solid #D32F2F', background: 'white',
      color: '#D32F2F', padding: '5px 10px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
    }}
  >
    <Delete sx={{fontSize: 16}}/>Delete
  </button>
);

type SizeLook = 'stroke' | 'line' | 'text' | 'eraser' | {sticker: StickerKind};

const sizeLook = (state: PhotoMarkupState): SizeLook | null => {
  const m = state.selected;
  if (m) {
    if (m.type === 'stroke') return 'stroke';
    if (m.type === 'line') return 'line';
    if (m.type === 'text') return 'text';
    return {sticker: m.kind};
  }
  return state.tool === 'draw' ? 'stroke' : state.tool === 'erase' ? 'eraser' : null;
};

const BottomPanel = ({state, enabled}: {state: PhotoMarkupState; enabled: boolean}) => {
  const selected = state.selected;
  const editing = state.editingText;
  let top: React.ReactNode;
  if (editing) {
    // Typing: just the look of the words.
    top = (
      <>
        <StyleRow state={state} showColors look="text"/>
        <div style={{display: 'flex', alignItems: 'center', gap: 8, marginTop: 6}}>
          <DeleteChip onClick={() => state.deleteSelected()}/>
          <Hint>Press Enter or click the photo when you're done typing.</Hint>
        </div>
      </>
    );
  } else if (state.showStickers) {
    top = <StickerTray state={state}/>;
  } else if (selected) {
    top = (
      <>
        <StyleRow state={state} showColors={colorOf(selected) !== null} look={sizeLook(state)}/>
        <div style={{display: 'flex', alignItems: 'center', gap: 8, marginTop: 6}}>
          <DeleteChip onClick={() => state.deleteSelected()}/>
          <Hint>{selected.type === 'line' ? 'Drag the ends to stretch and turn it.'
            : selected.type === 'stroke' ? 'Drag to move it.'
              : selected.type === 'text' ? 'Click the words to type. Drag to move.'
                : canTurn(selected) ? 'Drag to move. Use the turn arrow to spin it.' : 'Drag to move it.'}</Hint>
        </div>
      </>
    );
  } else if (state.tool === 'draw') {
    top = <StyleRow state={state} showColors look="stroke"/>;
  } else if (state.tool === 'erase') {
    top = <StyleRow state={state} showColors={false} look="eraser" hint="Push the pink end over a drawing to erase it."/>;
  } else if (!state.toolChosen) {
    top = <div style={{color: SUBTLE, fontSize: 12, fontWeight: 600, textAlign: 'center'}}>Select a tool</div>;
  } else {
    top = <Hint>Click a mark to change it. Scroll, or pinch with two fingers, to zoom.</Hint>;
  }
  const gap = !state.toolChosen && !selected && !state.showStickers ? 4 : 8;
  return (
    <div style={{background: PANEL_BG, borderRadius: '16px 16px 0 0', padding: '8px 10px', paddingBottom: 'calc(8px + env(safe-area-inset-bottom))'}}>
      <div style={{maxWidth: 560, margin: '0 auto'}}>
        {top}
        {!editing && (
          <div style={{display: 'flex', gap: 4, marginTop: gap}}>
            <ToolButton label="Move" selected={state.toolChosen && state.tool === 'move' && !state.showStickers} enabled={enabled}
                        onClick={() => state.chooseTool('move')} icon={c => <MoveIcon color={c}/>}/>
            <ToolButton label="Draw" selected={state.tool === 'draw'} enabled={enabled}
                        onClick={() => state.chooseTool('draw')} icon={c => <Create sx={{fontSize: 20, color: c}}/>}/>
            <ToolButton label="Erase" selected={state.tool === 'erase'} enabled={enabled}
                        onClick={() => state.chooseTool('erase')} icon={c => <EraserIcon color={c}/>}/>
            <ToolButton label="Text" selected={false} enabled={enabled} onClick={() => state.addText()}
                        icon={c => <span style={{color: c, fontSize: 16, fontWeight: 700}}>Aa</span>}/>
            {/* "Symbols" with an arrow: what the tray holds (arrow, cut line, X, cone, signs…). */}
            <ToolButton label="Symbols" selected={state.showStickers} enabled={enabled}
                        onClick={() => state.toggleStickers()} icon={c => <ArrowForward sx={{fontSize: 20, color: c}}/>}/>
            <ToolButton label="Rotate" selected={false} enabled={enabled} onClick={() => state.rotatePhoto()}
                        icon={c => <Refresh sx={{fontSize: 20, color: c}}/>}/>
          </div>
        )}
      </div>
    </div>
  );
};

/** The four colors (or a hint), then "Size" and three sizes. */
const StyleRow = ({state, showColors, look, hint}: {state: PhotoMarkupState; showColors: boolean; look: SizeLook | null; hint?: string}) => {
  const current = state.highlightedSize();
  return (
    <div style={{display: 'flex', alignItems: 'center'}}>
      {showColors ? (
        <>
          {MARK_COLORS.map(c => {
            const picked = state.shownColor === c;
            return (
              <button
                key={c}
                type="button"
                aria-label={COLOR_NAME[c]}
                // Keeps the cursor in a text box being typed into (as the phone keeps its keyboard).
                onMouseDown={e => e.preventDefault()}
                onClick={() => state.chooseColor(c)}
                style={{
                  width: 32, height: 32, marginRight: 6, borderRadius: '50%', cursor: 'pointer', boxSizing: 'border-box',
                  border: picked ? `2.5px solid ${INK}` : '1px solid rgba(0,0,0,0.2)', padding: picked ? 4 : 2, background: 'white',
                }}
              >
                <span style={{display: 'block', width: '100%', height: '100%', borderRadius: '50%', background: COLOR_HEX[c]}}/>
              </button>
            );
          })}
          <div style={{flex: 1}}/>
        </>
      ) : hint ? <Hint style={{flex: 1}}>{hint}</Hint> : <div style={{flex: 1}}/>}
      {look && (
        <>
          <span style={{color: SUBTLE, fontSize: 12, fontWeight: 600}}>Size</span>
          {MARK_SIZES.map(s => {
            const picked = current === s;
            return (
              <button
                key={s}
                type="button"
                aria-label={`Size ${SIZE_NAME[s]}`}
                onMouseDown={e => e.preventDefault()}
                onClick={() => state.chooseSize(s)}
                style={{
                  width: 34, height: 34, marginLeft: 5, borderRadius: '50%', cursor: 'pointer', boxSizing: 'border-box', padding: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: picked ? 'white' : '#EFEFEF', border: picked ? `2.5px solid ${INK}` : 'none',
                }}
              >
                <SizeGlyph look={look} size={s} color={state.shownColor}/>
              </button>
            );
          })}
        </>
      )}
    </div>
  );
};

const SIZE_NAME: Record<MarkSize, string> = {s: 'Small', m: 'Medium', l: 'Large'};

/** One size button's picture, in the color the mark will be. */
const SizeGlyph = ({look, size, color}: {look: SizeLook; size: MarkSize; color: MarkColor}) => {
  const step = MARK_SIZES.indexOf(size);
  const fill = COLOR_HEX[color];
  if (look === 'text') {
    const fs = [12, 16, 21][step];
    return <span style={{font: `bold ${fs}px ${TEXT_FONT_FAMILY}`, color: fill, WebkitTextStroke: `2px ${OUTLINE}`, paintOrder: 'stroke fill'}}>A</span>;
  }
  if (typeof look === 'object') {
    const px = [14, 20, 27][step];
    return <StickerPreview kind={look.sticker} color={fill} size={px}/>;
  }
  if (look === 'eraser') {
    const w = [9, 15, 24][step];
    const h = w * ERASER_ASPECT;
    return (
      <svg width={28} height={28} viewBox="0 0 28 28">
        <rect x={14 - w / 2} y={14 - h / 2} width={w} height={h} rx={h * 0.28} fill={ERASER_PINK} stroke={INK} strokeWidth={1.5}/>
      </svg>
    );
  }
  // A short piece of line as thick as that size draws.
  const w = (look === 'stroke' ? [2, 4, 7] : [3, 5, 8])[step];
  return (
    <svg width={24} height={24} viewBox="0 0 24 24">
      <line x1={3.6} y1={16.8} x2={20.4} y2={7.2} stroke={OUTLINE} strokeWidth={w + 2} strokeLinecap="round"/>
      <line x1={3.6} y1={16.8} x2={20.4} y2={7.2} stroke={fill} strokeWidth={w} strokeLinecap="round"/>
    </svg>
  );
};

/** Ready-made marks to add: tap (click) one to put it in the middle of the view. */
const StickerTray = ({state}: {state: PhotoMarkupState}) => {
  const items = STICKER_KINDS.filter(k => STICKERS[k].inTray);
  const tint = COLOR_HEX[state.color];
  return (
    <>
      <Hint>Click one to add it</Hint>
      <div style={{display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 5, marginTop: 4}}>
        {items.map(kind => (
          <button
            key={kind}
            type="button"
            onClick={() => state.addSticker(kind)}
            style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', border: 'none', borderRadius: 10,
              background: TOOL_BG, padding: '4px 0', cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            <StickerPreview kind={kind} color={tint} size={32}/>
            <span style={{color: INK, fontSize: 11, fontWeight: 600}}>{STICKERS[kind].label}</span>
          </button>
        ))}
      </div>
    </>
  );
};

const StickerPreview = ({kind, color, size}: {kind: StickerKind; color: string; size: number}) => {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const c = ref.current;
    const ctx = c?.getContext('2d');
    if (!c || !ctx) return;
    const dpr = window.devicePixelRatio || 1;
    c.width = size * dpr;
    c.height = size * dpr;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.translate(size / 2, size / 2);
    ctx.scale(size * 0.9, size * 0.9);
    drawStickerUnit(ctx, kind, color);
  }, [kind, color, size]);
  return <canvas ref={ref} style={{width: size, height: size, display: 'block'}}/>;
};

const ToolButton = ({label, selected, enabled, onClick, icon}: {
  label: string; selected: boolean; enabled: boolean; onClick: () => void; icon: (color: string) => React.ReactNode;
}) => {
  const tint = !enabled ? 'rgba(34,34,34,0.33)' : selected ? '#FFFFFF' : INK;
  return (
    <button
      type="button"
      disabled={!enabled}
      onClick={onClick}
      style={{
        flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', border: 'none', borderRadius: 12,
        background: selected ? GREEN : TOOL_BG, padding: '5px 0', cursor: enabled ? 'pointer' : 'default', fontFamily: 'inherit',
      }}
    >
      {/* The picture is decoration: the button's name is its label ("Text", not "Aa Text"). */}
      <span aria-hidden style={{width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>{icon(tint)}</span>
      <span style={{color: tint, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap'}}>{label}</span>
    </button>
  );
};

/** A tilted block eraser with its rubber tip shaded. */
const EraserIcon = ({color}: {color: string}) => (
  <svg width={20} height={20} viewBox="0 0 20 20">
    <g transform="rotate(-40 10 10)">
      <rect x={5.8} y={1.0 + 17.2 - 6.5} width={8.4} height={6.5} rx={1.8} fill={color}/>
      <rect x={5.8} y={1.0} width={8.4} height={17.2} rx={1.8} fill="none" stroke={color} strokeWidth={1.8}/>
    </g>
    <line x1={9} y1={19} x2={19} y2={19} stroke={color} strokeWidth={1.8} strokeLinecap="round"/>
  </svg>
);

/** Four-way arrows: "move things". */
const MoveIcon = ({color}: {color: string}) => {
  const w = 22;
  const c = w / 2;
  const a = w * 0.16;
  const sw = w * 0.09;
  const tips: [Point, Point][] = [
    [{x: c, y: w * 0.06}, {x: 0, y: 1}],
    [{x: c, y: w * 0.94}, {x: 0, y: -1}],
    [{x: w * 0.06, y: c}, {x: 1, y: 0}],
    [{x: w * 0.94, y: c}, {x: -1, y: 0}],
  ];
  return (
    <svg width={w} height={w} viewBox={`0 0 ${w} ${w}`} stroke={color} strokeWidth={sw} strokeLinecap="round" fill="none">
      <line x1={c} y1={w * 0.06} x2={c} y2={w * 0.94}/>
      <line x1={w * 0.06} y1={c} x2={w * 0.94} y2={c}/>
      {tips.map(([tip, dir], i) => {
        const back = {x: tip.x + dir.x * a, y: tip.y + dir.y * a};
        return (
          <g key={i}>
            <line x1={tip.x} y1={tip.y} x2={back.x + dir.y * a} y2={back.y + dir.x * a}/>
            <line x1={tip.x} y1={tip.y} x2={back.x - dir.y * a} y2={back.y - dir.x * a}/>
          </g>
        );
      })}
    </svg>
  );
};

export default PhotoEditor;
